import { ArrowUp } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { initials } from '@/lib/publicText'
import { PUBLIC_CONTAINER } from './publicShell'

export interface FooterLink {
  label: string
  href: string
  icon?: LucideIcon
  /** Abre em nova aba com rel de segurança. */
  external?: boolean
  /**
   * Disparado no clique, antes de navegar. Existe para o link de WhatsApp do
   * rodapé contar como conversão igual aos outros CTAs — sem isto ele seria o
   * único caminho para o WhatsApp sem evento.
   */
  onClick?: () => void
}

export interface FooterColumn {
  title: string
  links: FooterLink[]
}

export interface SocialLink {
  label: string
  href: string
  icon: LucideIcon
}

interface PublicFooterProps {
  /** Bloco de marca à esquerda. Na landing é a escola; no diretório, a BeWave. */
  brand: {
    name: string
    /**
     * Assinatura sob a marca. Opcional: o rodapé da plataforma não tem mais —
     * "Menos planilha. Mais tempo na água." é a promessa do produto de GESTÃO, e
     * a BeWave hoje são dois (o admin da escola e o Explore). Uma frase que fala
     * por um deles no rodapé dos dois diria a coisa errada em metade das páginas.
     */
    tagline?: string
    color: string
    /**
     * `false` esconde o bloco de marca inteiro e joga a assinatura para a barra
     * de baixo. É o que o rodapé da plataforma faz: o header é sticky e carrega
     * a logo da BeWave o tempo todo, então repeti-la aqui só gasta altura.
     * A landing da escola mantém `true` — ali a marca do rodapé é a DELA, e não
     * aparece em lugar nenhum do topo.
     */
    showMark?: boolean
    /** Avatar quadrado (escola). Sem ele, cai nas iniciais sobre `color`. */
    logoUrl?: string | null
    /**
     * Logo horizontal da plataforma. Tem precedência sobre `logoUrl` e é
     * renderizada com largura automática — o quadrado com object-cover que serve
     * ao avatar da escola cortaria uma logo 3:1.
     */
    wordmarkUrl?: string | null
  }
  columns: FooterColumn[]
  /** Barra inferior: termos, privacidade, contato. */
  legalLinks?: FooterLink[]
  social?: SocialLink[]
  copyright: string
  /**
   * Reserva espaço embaixo para barra fixa no mobile — a MobileBar na landing, o
   * toggle lista/mapa no diretório. Sem isso o conteúdo final fica escondido.
   */
  clearsFixedBar?: boolean
  /** Escondê-lo no mobile, onde a folha da lista cobre a tela. */
  className?: string
}

function FooterAnchor({ link }: { link: FooterLink }) {
  const Icon = link.icon

  return (
    <a
      href={link.href}
      onClick={link.onClick}
      {...(link.external
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {})}
      className="inline-flex w-fit items-center gap-2 transition-colors hover:text-gray-900"
    >
      {Icon && <Icon className="h-4 w-4" />}
      {link.label}
    </a>
  )
}

/**
 * Rodapé das páginas públicas, no formato de diretório: bloco de marca à esquerda,
 * colunas de links com título em caixa alta, e uma barra inferior separada com os
 * links legais de um lado e as redes do outro.
 *
 * Só o esqueleto é compartilhado — o conteúdo vem por slot, porque a landing fala
 * de UMA escola (âncoras internas, WhatsApp dela) e o diretório fala da plataforma.
 * Usa o mesmo container do header: quando os dois divergiam, a logo do topo e a do
 * rodapé não alinhavam e a página parecia montada em dois moldes.
 */
export default function PublicFooter({
  brand,
  columns,
  legalLinks = [],
  social = [],
  copyright,
  clearsFixedBar = false,
  className,
}: PublicFooterProps) {
  return (
    <footer
      className={cn(
        'border-t border-gray-200 bg-white pt-8 sm:pt-10',
        clearsFixedBar ? 'pb-28 lg:pb-10' : 'pb-6',
        className,
      )}
    >
      <div
        className={cn(
          PUBLIC_CONTAINER,
          'flex flex-col gap-8 md:flex-row md:items-start md:justify-between',
        )}
      >
        {brand.showMark !== false && (
          <div className="max-w-xs">
            {brand.wordmarkUrl ? (
              <img
                src={brand.wordmarkUrl}
                alt={brand.name}
                className="h-9 w-auto"
              />
            ) : brand.logoUrl ? (
              <img
                src={brand.logoUrl}
                alt={brand.name}
                className="h-10 w-10 rounded-xl object-cover"
              />
            ) : (
              <span
                className="flex h-10 w-10 items-center justify-center rounded-xl text-sm font-bold text-white"
                style={{ backgroundColor: brand.color }}
              >
                {initials(brand.name)}
              </span>
            )}
            {brand.tagline && (
              <p className="mt-3 text-sm text-gray-500">{brand.tagline}</p>
            )}
          </div>
        )}

        {/* Quatro colunas cabem numa linha só a partir de `md`. Em três, a
            quarta descia para uma segunda fileira e o rodapé ganhava 110px de
            altura sem ganhar informação. */}
        <div className="grid flex-1 grid-cols-2 gap-x-8 gap-y-6 sm:grid-cols-3 md:grid-cols-4">
          {columns.map((column) => (
            <nav key={column.title} className="flex flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-900">
                {column.title}
              </p>
              <div className="flex flex-col gap-2 text-sm text-gray-600">
                {column.links.map((link) => (
                  <FooterAnchor
                    key={`${link.label}-${link.href}`}
                    link={link}
                  />
                ))}
              </div>
            </nav>
          ))}
        </div>
      </div>

      <div
        className={cn(PUBLIC_CONTAINER, 'mt-8 border-t border-gray-200 pt-5')}
      >
        {/* Uma linha só, embrulhando: no mobile o `flex-col` empilhava
            assinatura, legais e redes em três alturas e a barra sozinha passava
            de 130px. */}
        <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-3">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-600">
            {/* Sem o bloco de marca, a assinatura vem para cá — quando existe. */}
            {brand.showMark === false && brand.tagline && (
              <span className="text-gray-500">{brand.tagline}</span>
            )}
            {legalLinks.map((link) => (
              <FooterAnchor key={`${link.label}-${link.href}`} link={link} />
            ))}
          </div>

          <div className="flex items-center gap-4">
            {/* Voltar ao topo, ao lado das redes.
                O diretório é comprido — sete seções de carrossel mais o banner —
                e chegar aqui embaixo por rolagem não tinha volta a não ser
                rolando tudo de novo. Fica no rodapé e não flutuando: um botão
                fixo cobriria conteúdo a página inteira para servir a um único
                momento, que é justamente o fim dela. */}
            <VoltarAoTopo />

            {social.length > 0 && (
              <>
                {social.map(({ label, href, icon: Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="text-gray-500 transition-colors hover:text-gray-900"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                ))}
              </>
            )}
          </div>
        </div>

        <p className="mt-3 text-xs text-gray-400">{copyright}</p>
      </div>
    </footer>
  )
}

/**
 * Volta ao topo da página.
 *
 * `scroll-behavior` da preferência do sistema: para quem pediu menos movimento,
 * o salto é instantâneo — uma página inteira passando voando é exatamente o tipo
 * de animação que essa preferência existe para evitar.
 */
function VoltarAoTopo() {
  return (
    <button
      type="button"
      onClick={() =>
        window.scrollTo({
          top: 0,
          behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)')
            .matches
            ? 'auto'
            : 'smooth',
        })
      }
      className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-900"
    >
      <ArrowUp className="size-3.5" aria-hidden="true" />
      Voltar ao topo
    </button>
  )
}
